import { AnyObject, FORM_ERROR } from 'final-form';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  baaSchema,
  BAAView,
  organizationSchema,
  OrganizationView,
  PrescriberView,
  termsAndConditionsSchema,
  TermsAndConditionsView
} from '.';
import { useNewRegistration } from '../api/registration.generated';
import { FormWizard } from '../components';
import { AppRootState, navigateToLogin, navigateToRegistrationSubmitted, tenancyFeatureSelector } from '../redux';
import { DBFieldSchema, FORM_WIZARD_VALUES, validateFormValues } from '../utils';
import { getOrganizationOnNextSchema } from './Organization';
import { AnyType } from '../interfaces';

const REGISTRATION_FORM_PAGE_NAMES = FORM_WIZARD_VALUES?.pages?.registration?.names;

export interface RegistrationFormProps {
  isLoading: boolean;
  formFields: DBFieldSchema;
}

export const RegistrationView = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLoader, setShowLoader] = useState(false); // New state for delayed loader
  const applicationName = useSelector((state: AppRootState) => state.app.config?.applicationName);
  const programId = useSelector((state: AppRootState) => state.app.config?.programId);
  const dispatch = useDispatch();
  const { mutate: submitRegistration } = useNewRegistration({});
  // Getting the steps object from 'registration.steps' feature
  const stepsObject: AnyType = useSelector<AnyType>(tenancyFeatureSelector('registration.steps'));

  const onSubmit = async (values: AnyObject) => {
    setIsSubmitting(true);
    setShowLoader(true);

    const result = await submitRegistration({
      PracticeInfo: {
        ProgramId: Number(programId),
        PracticeName: values.organization_name,
        TaxId: values.tax_id,
        NPI: String(values.organization_npi),
        AddressLine1: values.address_1,
        AddressLine2: values.address_2,
        City: values.city,
        State: values.state,
        Zip: values.zip,
        PhoneNumber: values.organization_phone,
        FaxNumber: values.organization_fax
      },
      AdministratorInfo: {
        IsPhysician: values.administrator_is_prescriber === 'yes' ? 1 : 0,
        NPI: values.administrator_prescriber_npi ? String(values.administrator_prescriber_npi) : '',
        FirstName: values.administrator_first_name,
        LastName: values.administrator_last_name,
        Suffix: values.administrator_suffix,
        PhoneNumber: values.administrator_phone,
        FaxNumber: values.administrator_fax,
        EMail: values.administrator_email,
        UserName: values.administrator_username
      },
      PhysicianInfo: {
        NPI: String(values.prescriber_npi),
        FirstName: values.prescriber_first_name,
        LastName: values.prescriber_last_name,
        MiddleName: values.prescriber_middle_name,
        TaxID: values.prescriber_tax_id,
        StateLicenseNumber: values.prescriber_state_license,
        LicensingState: values.prescriber_licensing_state,
        DEA: values.prescriber_dea,
        PTAN: values.prescriber_ptan,
        EMAIL: values.prescriber_email,
        HIN: values.prescriber_hin
      },
      TermsAndConditionsAcknowledgement: values.acknowledge_terms,
      BAAAcknowledgement: false,
      BAALanguageAcknowledgement: values.acknowledge_language,
      BAASignatureText: values.signature,
      BAASignatureDate: new Date(values.signature_date)
    });

    setIsSubmitting(false);
    setShowLoader(false);

    if (result?.isSuccess) {
      dispatch(navigateToRegistrationSubmitted());
      return;
    }

    const defaultError = 'There was a problem registering the user. Please contact support.';
    const error = result.errors?.map((e) => e?.description).join(' ');

    return { [FORM_ERROR]: error ?? defaultError };
  };

  return (
    <div>
      <FormWizard
        isLastPage={stepsObject?.steps?.baa?.hide ? true : false}
        actionsClassName='container'
        initialValues={{
          ...organizationSchema({ programId }).default(),
          ...termsAndConditionsSchema.default(),
          ...baaSchema.default()
        }}
        onCancel={() => dispatch(navigateToLogin())}
        onSubmit={onSubmit}>
        <FormWizard.Page
          name={REGISTRATION_FORM_PAGE_NAMES.organization}
          onFormNext={validateFormValues(getOrganizationOnNextSchema(applicationName))}
          validate={validateFormValues(organizationSchema({ programId }))}>
          <OrganizationView />
        </FormWizard.Page>
        <FormWizard.Page>
          <></>
        </FormWizard.Page>
      </FormWizard>
      {showLoader && isSubmitting && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(229, 231, 235, 0.75)',
            zIndex: 50
          }}>
          <img src='/images/spinner.gif' alt='Loading...' className='w-20 h-20' />
        </div>
      )}
    </div>
  );
};
